"use client";

import { useState, type DragEvent, type ReactNode } from "react";
import clsx from "clsx";
import type { AniFileDropHandler } from "@/context/AniContext";

/** Lets the user drop a file onto Ani when a feature has registered a handler; otherwise renders children untouched. */
export function AniFileDropZone({ handler, children }: { handler: AniFileDropHandler | null; children: ReactNode }) {
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!handler) return <>{children}</>;

  const hasFiles = (event: DragEvent) => Array.from(event.dataTransfer.types).includes("Files");

  return (
    <div
      className="contents"
      onDragOver={(event) => {
        if (!hasFiles(event)) return;
        event.preventDefault();
        setDragging(true);
      }}
      onDragLeave={(event) => {
        if (!event.currentTarget.parentElement?.contains(event.relatedTarget as Node | null)) setDragging(false);
      }}
      onDrop={(event) => {
        if (!hasFiles(event)) return;
        event.preventDefault();
        setDragging(false);
        const file = event.dataTransfer.files[0];
        if (!file || !handler.accepts.some((extension) => file.name.toLowerCase().endsWith(extension))) return;
        setBusy(true);
        void handler.onFile(file).finally(() => setBusy(false));
      }}
    >
      {children}
      {(dragging || busy) && (
        <div
          className={clsx(
            "pointer-events-none absolute inset-0 z-30 flex items-center justify-center rounded-[inherit] border-2 border-dashed",
            "border-violet-400 bg-violet-50/80 text-violet-700 dark:border-violet-400/70 dark:bg-violet-950/70 dark:text-violet-200",
          )}
          data-testid="ani-file-drop-overlay"
        >
          <p className="text-sm font-medium">{busy ? "Reading the file…" : handler.label}</p>
        </div>
      )}
    </div>
  );
}
